<div class="pd-24 component-container bookings-page">
  <div class="fg-1">
    <div class="card inverse-bg-transparent">
      <!-- The bar spans the three tabs, but each control is shown only on the tabs that actually
           read it — the calendar owns its period through its own nav, and the usage report is
           grouped server-side over the whole branch, so rooms, status and search never reach it. -->
      <div class="bookings-toolbar">
        <div class="bookings-filters">
          @if (showDateFilter) {
            <div class="filter-field filter-date">
              <label class="filter-label" for="bookings-range">{{ 'BOOKINGS.FILTERS.DATE_RANGE' | translate }}</label>
              <p-datepicker
                inputId="bookings-range"
                selectionMode="range"
                dateFormat="dd/mm/yy"
                [readonlyInput]="true"
                [showButtonBar]="true"
                [showIcon]="true"
                iconDisplay="input"
                [(ngModel)]="dateRange"
                (onClose)="onFiltersChange()"
              ></p-datepicker>
            </div>
          }

          <!-- Only worth the space when the client HAS more than one branch. -->
          @if (hasMultipleBranches) {
            <div class="filter-field">
              <label class="filter-label" for="bookings-branch">{{ 'BOOKINGS.FILTERS.BRANCH' | translate }}</label>
              <p-select
                inputId="bookings-branch"
                optionLabel="label"
                optionValue="value"
                [options]="branchOptions"
                [(ngModel)]="selectedEventId"
                [showClear]="true"
                [placeholder]="'BOOKINGS.FILTERS.ALL_BRANCHES' | translate"
                (onChange)="onBranchChange()"
              ></p-select>
            </div>
          }

          @if (showRoomFilter) {
            <div class="filter-field">
              <label class="filter-label" for="bookings-rooms">{{ 'BOOKINGS.FILTERS.ROOMS' | translate }}</label>
              <!-- One-way binding plus an explicit handler: the clear button writes null over the
                   model, and the calendar reads this list on every redraw. -->
              <p-multiSelect
                inputId="bookings-rooms"
                optionLabel="name"
                optionValue="area_id"
                [options]="branchAreas"
                [ngModel]="selectedAreaIds"
                (ngModelChange)="onRoomsChange($event)"
                [filter]="true"
                [showClear]="true"
                [maxSelectedLabels]="1"
                [selectedItemsLabel]="'BOOKINGS.FILTERS.ROOMS_SELECTED' | translate"
                [placeholder]="'BOOKINGS.FILTERS.ALL_ROOMS' | translate"
                (onPanelHide)="onFiltersChange()"
                (onClear)="onFiltersChange()"
              ></p-multiSelect>
            </div>
          }

          @if (showStatusFilter) {
            <div class="filter-field">
              <label class="filter-label" for="bookings-status">{{ 'BOOKINGS.FILTERS.STATUS' | translate }}</label>
              <p-select
                inputId="bookings-status"
                optionLabel="label"
                optionValue="value"
                [options]="statusOptions"
                [(ngModel)]="selectedStatus"
                [showClear]="true"
                [placeholder]="'BOOKINGS.FILTERS.ALL_STATUSES' | translate"
                (onChange)="onFiltersChange()"
              ></p-select>
            </div>
          }

          @if (showSearchFilter) {
            <div class="filter-field filter-search">
              <label class="filter-label" for="bookings-search">{{ 'BOOKINGS.FILTERS.SEARCH' | translate }}</label>
              <span class="filter-search-box">
                <i aria-hidden="true" class="pi pi-search"></i>
                <!-- Filters the list, highlights the calendar — the placeholder says which. -->
                <input id="bookings-search" type="text" pInputText [(ngModel)]="search" (input)="onSearchChange()" [placeholder]="searchPlaceholderKey | translate" />
              </span>
            </div>
          }
        </div>

        <button type="button" class="btn btn-primary bookings-new" *hasPermission="permissions.bookings.groups.general.components.create" (click)="create()">
          <i aria-hidden="true" class="pi pi-plus"></i>
          {{ 'BOOKINGS.ACTIONS.NEW' | translate }}
        </button>
      </div>

      <p-tabs [value]="activeTab" (valueChange)="onTabChange($event)">
        <!-- Calendar first: the page is opened to see what the rooms are doing, which is a picture
             rather than a list. -->
        <p-tablist>
          <p-tab [value]="tabs.calendar">{{ 'BOOKINGS.TABS.CALENDAR' | translate }}</p-tab>
          <p-tab [value]="tabs.grid">{{ 'BOOKINGS.TABS.GRID' | translate }}</p-tab>
          <p-tab [value]="tabs.usage">{{ 'BOOKINGS.TABS.USAGE' | translate }}</p-tab>
        </p-tablist>

        <p-tabpanels>
          <!-- ─────────────────────────── Calendar ─────────────────────────── -->
          <p-tabpanel [value]="tabs.calendar">
            <div class="calendar-toolbar">
              <!-- Day or week. A client with one or two rooms spends the room axis on a constant,
                   so the week view gives that space to the axis that actually varies. -->
              <div class="calendar-modes" role="group" [attr.aria-label]="'BOOKINGS.TABS.CALENDAR' | translate">
                <button type="button" class="calendar-mode" [class.is-active]="calendarMode === 'day'" [attr.aria-pressed]="calendarMode === 'day'" (click)="setCalendarMode('day')">
                  {{ 'BOOKINGS.CALENDAR.DAY' | translate }}
                </button>
                <button
                  type="button"
                  class="calendar-mode"
                  [class.is-active]="calendarMode === 'week'"
                  [attr.aria-pressed]="calendarMode === 'week'"
                  (click)="setCalendarMode('week')"
                >
                  {{ 'BOOKINGS.CALENDAR.WEEK' | translate }}
                </button>
              </div>

              <div class="calendar-nav">
                <!-- The chevrons are mirrored in RTL by CSS: the flex row already puts "previous"
                     on the right, so only the glyph itself has to flip. -->
                <button
                  type="button"
                  class="calendar-nav-btn"
                  [title]="(calendarMode === 'week' ? 'BOOKINGS.CALENDAR.PREVIOUS_WEEK' : 'BOOKINGS.CALENDAR.PREVIOUS_DAY') | translate"
                  (click)="shiftCalendarDay(-1)"
                >
                  <i aria-hidden="true" class="pi pi-chevron-left"></i>
                  <span class="sr-only">{{ (calendarMode === 'week' ? 'BOOKINGS.CALENDAR.PREVIOUS_WEEK' : 'BOOKINGS.CALENDAR.PREVIOUS_DAY') | translate }}</span>
                </button>
                <!-- dir="auto": the day heading starts with a localised weekday, the week heading
                     with a date. `auto` reads the first strong character and gets both right. -->
                <span class="calendar-date" dir="auto">{{ calendarDateLabel }}</span>
                <button
                  type="button"
                  class="calendar-nav-btn"
                  [title]="(calendarMode === 'week' ? 'BOOKINGS.CALENDAR.NEXT_WEEK' : 'BOOKINGS.CALENDAR.NEXT_DAY') | translate"
                  (click)="shiftCalendarDay(1)"
                >
                  <i aria-hidden="true" class="pi pi-chevron-right"></i>
                  <span class="sr-only">{{ (calendarMode === 'week' ? 'BOOKINGS.CALENDAR.NEXT_WEEK' : 'BOOKINGS.CALENDAR.NEXT_DAY') | translate }}</span>
                </button>
              </div>

              <button type="button" class="calendar-today" [disabled]="isCalendarToday" (click)="goToToday()">
                {{ (calendarMode === 'week' ? 'BOOKINGS.CALENDAR.THIS_WEEK' : 'BOOKINGS.CALENDAR.TODAY') | translate }}
              </button>

              <ul class="calendar-legend">
                @for (entry of calendarLegend; track entry.status) {
                  <li><span class="legend-swatch" [class]="'is-' + entry.status"></span>{{ entry.labelKey | translate }}</li>
                }
              </ul>

              <!-- Only when the admin's own clock disagrees — otherwise it is a badge saying
                   "these times are the times". -->
              @if (bookingTime.differsFromBrowser) {
                <span class="calendar-tz" [title]="'BOOKINGS.CALENDAR.TIMEZONE_HINT' | translate">
                  <i aria-hidden="true" class="pi pi-globe"></i>
                  <span dir="ltr">{{ bookingTime.label }}</span>
                </span>
              }
            </div>

            @if (calendarRows.length) {
              <div class="calendar" [class.is-grouped]="calendarGrouped">
                <div class="calendar-scroller">
                  <div class="calendar-header">
                    <div class="calendar-room-col"></div>
                    <div class="calendar-track">
                      @for (hour of calendarHourColumns; track hour) {
                        <span class="calendar-hour">{{ hour }}:00</span>
                      }
                    </div>
                  </div>

                  @for (row of calendarRows; track row.key) {
                    <!-- The day is a band across the whole width, not a first line squeezed into the
                         first room's cell — which is what made one room's name look unlike the next
                         room's. Its label sticks to the leading edge as the hours scroll past. -->
                    @if (row.groupLabel) {
                      <div class="calendar-day-head" [class.is-today]="row.isToday">
                        <span dir="auto">{{ row.groupLabel }}</span>
                      </div>
                    }
                    <div class="calendar-row" [class.is-today]="row.isToday">
                      <!-- dir="auto" so a truncated name is clipped at its own end: without it an
                           English room name inside the Hebrew layout loses its FIRST characters. -->
                      <div class="calendar-room-col" [title]="row.title">
                        @if (row.rowLabel) {
                          <span class="calendar-room-name" dir="auto">{{ row.rowLabel }}</span>
                        }
                        @if (row.branchLabel) {
                          <span class="calendar-room-branch" dir="auto">{{ row.branchLabel }}</span>
                        }
                      </div>
                      <!-- The blocks are absolutely positioned, so a track full of them will not
                           grow by itself: the lane count has to reach the height as a variable. -->
                      <div class="calendar-track" [class.is-stacked]="row.lanes > 1" [style.--lanes]="row.lanes">
                        @for (hour of calendarHourColumns; track hour) {
                          <span class="calendar-gridline"></span>
                        }
                        <!-- Hours the room is shut. Shading them is what tells "nothing booked yet"
                             apart from "cannot be booked", which an empty row alone never did. -->
                        @for (shut of row.closed; track shut.leftPct) {
                          <span class="calendar-closed" [style.inset-inline-start.%]="shut.leftPct" [style.width.%]="shut.widthPct"></span>
                        }
                        <!-- Booked, then handed back when the meeting ended early. Outlined and not
                             filled, and drawn before the free-time hit areas so it can never take a
                             click off one: the room was EMPTY here, and the click that books it is
                             the true thing about this stretch. -->
                        @for (back of row.released; track back.leftPct) {
                          <span class="calendar-released" [style.inset-inline-start.%]="back.leftPct" [style.width.%]="back.widthPct"></span>
                        }
                        @if (row.isToday && calendarNowPct !== null) {
                          <span class="calendar-now" [style.inset-inline-start.%]="calendarNowPct"></span>
                        }
                        <!-- Free time is the booking control, not blank space: clicking a stretch
                             opens the drawer on that room, that day and the hour clicked. The
                             stretch itself stays invisible — what lights up is the slot below,
                             which is the part the click would actually take. -->
                        @for (gap of row.gaps; track gap.leftPct) {
                          <button
                            type="button"
                            class="calendar-gap"
                            *hasPermission="permissions.bookings.groups.general.components.create"
                            [style.inset-inline-start.%]="gap.leftPct"
                            [style.width.%]="gap.widthPct"
                            [title]="'BOOKINGS.CALENDAR.BOOK_HERE' | translate"
                            (mousemove)="onGapHover(row, gap, $event)"
                            (mouseleave)="clearGapHover()"
                            (click)="bookGap(row, gap, $event)"
                          >
                            <span class="sr-only">{{ 'BOOKINGS.CALENDAR.BOOK_HERE' | translate }}</span>
                          </button>
                        }
                        @if (gapHover && gapHover.rowKey === row.key) {
                          <span class="calendar-ghost" [style.inset-inline-start.%]="gapHover.leftPct" [style.width.%]="gapHover.widthPct">
                            <span dir="ltr">{{ gapHover.label }}</span>
                          </span>
                        }
                        <!-- Clicking a block opens its details panel, not the edit drawer: the
                             question in front of an admin watching a room is far more often "whose
                             is this, and can I have it back?" than "let me retype its times". -->
                        @for (block of row.blocks; track block.booking.id) {
                          <button
                            type="button"
                            class="calendar-block"
                            [class]="'is-' + block.booking.status"
                            [class.is-dimmed]="block.dimmed"
                            [style.inset-inline-start.%]="block.leftPct"
                            [style.width.%]="block.widthPct"
                            [style.--lane]="block.lane"
                            [title]="block.tooltip"
                            (click)="openBooking(block.booking, $event)"
                          >
                            <span class="calendar-block-time" dir="ltr">{{ block.time }}</span>
                            <span class="calendar-block-label" dir="auto">{{ block.label }}</span>
                          </button>
                        }
                      </div>
                    </div>
                  }
                </div>
              </div>
            } @else {
              <p-message severity="info" class="w-stretch">{{ 'BOOKINGS.CALENDAR.NO_ROOMS' | translate }}</p-message>
            }

            <!-- One booking's details, beside the block it belongs to.
                 `appendTo="body"` because the calendar clips its own overflow — a panel drawn
                 inside the scroller would be sliced off at the first row it overhangs. That is why
                 its styles sit OUTSIDE the `.bookings-page` block in the SCSS: the element keeps
                 its encapsulation attribute when it moves to the body, but not its ancestors. -->
            <p-popover #blockPanel appendTo="body" styleClass="booking-panel" (onShow)="onBookingPanelShow()" (onHide)="onBookingPanelHide()">
              @if (selectedBooking; as booking) {
                <div class="booking-panel-body">
                  <!-- Every client-typed value in this panel is wrapped in <bdi> rather than carrying
                       dir="auto" itself. Both order the text correctly, but dir also decides which
                       EDGE the value aligns to — so on a Hebrew page a Latin name jumped to the far
                       side and the column zig-zagged line to line. <bdi> isolates the direction and
                       leaves the alignment with the page, which is the half we want. -->
                  <div class="booking-panel-head">
                    <span class="booking-panel-room">
                      <span class="booking-panel-room-name"><bdi>{{ booking.area_name || ('BOOKINGS.UNKNOWN_ROOM' | translate) }}</bdi></span>
                      <!-- The branch is what tells six rooms called "Room B" apart. -->
                      @if (hasMultipleBranches && booking.event_name) {
                        <span class="booking-panel-room-branch"><bdi>{{ booking.event_name }}</bdi></span>
                      }
                    </span>
                    <p-tag [severity]="statusSeverity(booking.status)" [value]="'BOOKINGS.STATUS.' + (booking.status | uppercase) | translate"></p-tag>
                  </div>

                  <!-- The time is the headline, not a row in the list below it: it is the reason the
                       panel was opened, and in the week view it is also the only place the DAY is
                       written down. start_wall / end_wall and dir="ltr" for the same two reasons as
                       the list — these are the CLIENT'S times, and bidi would print end–start. -->
                  <div class="booking-panel-when">
                    <bdi class="booking-panel-clock" dir="ltr">{{ booking.start_wall | date: 'HH:mm' }}&ndash;{{ booking.end_wall | date: 'HH:mm' }}</bdi>
                    <bdi class="booking-panel-date" dir="ltr">{{ booking.start_wall | date: 'dd/MM/yyyy' }}</bdi>
                  </div>

                  <!-- Why the room stood empty inside its own booking. The headline above is the
                       BOOKED window — the record, and what the credits were charged against — and
                       that window is exactly what an admin looking at two blocks in one hour is
                       trying to reconcile. So the room's side of it is written down, not left to be
                       inferred from where a block happens to stop. -->
                  @if (occupancyNote(booking); as note) {
                    <p class="booking-panel-note">{{ note }}</p>
                  }

                  <dl class="booking-panel-facts">
                    <div>
                      <dt>{{ 'BOOKINGS.FIELDS.BOOKED_BY' | translate }}</dt>
                      <dd><bdi>{{ booking.pass_name || '—' }}</bdi></dd>
                    </div>
                    <!-- Dropped rather than dashed: a panel is read top to bottom, and a line that
                         says nothing is a line the eye still has to spend. -->
                    @if (booking.client_role_name) {
                      <div>
                        <dt>{{ 'BOOKINGS.FIELDS.GROUP' | translate }}</dt>
                        <dd><bdi>{{ booking.client_role_name }}</bdi></dd>
                      </div>
                    }
                    @if (booking.title) {
                      <div>
                        <dt>{{ 'BOOKINGS.FIELDS.TITLE' | translate }}</dt>
                        <dd><bdi>{{ booking.title }}</bdi></dd>
                      </div>
                    }
                    @if (usesCredits) {
                      <div>
                        <dt>{{ creditsUnitKey | translate }}</dt>
                        <dd>
                          <bdi dir="ltr">{{ booking.credits_charged - booking.credits_refunded }}</bdi>
                          @if (booking.credits_refunded > 0) {
                            <span class="booking-panel-refunded">{{ 'BOOKINGS.FIELDS.REFUNDED' | translate }} <bdi dir="ltr">{{ booking.credits_refunded }}</bdi></span>
                          }
                        </dd>
                      </div>
                    }
                  </dl>

                  <!-- Every action puts the panel away before it runs: each raises a confirmation
                       dialog or the edit drawer on top of where the panel is standing, and a panel
                       left open behind a modal is still listening for the outside click that
                       dismisses it — so it would vanish under the reader's first click. -->
                  @if (hasActions(booking)) {
                    <div class="booking-panel-actions">
                      @if (canEdit(booking)) {
                        <button
                          type="button"
                          class="booking-panel-action"
                          *hasPermission="permissions.bookings.groups.general.components.create"
                          (click)="blockPanel.hide(); edit(booking)"
                        >
                          <i aria-hidden="true" class="icon icon-pencil"></i>
                          <span class="booking-panel-action-label">{{ 'COMMON.BUTTONS.EDIT' | translate }}</span>
                        </button>
                      }
                      @if (canEdit(booking)) {
                        <button
                          type="button"
                          class="booking-panel-action"
                          *hasPermission="permissions.bookings.groups.general.components.extend"
                          (click)="blockPanel.hide(); extend(booking)"
                        >
                          <i aria-hidden="true" class="pi pi-clock"></i>
                          <span class="booking-panel-action-label">{{ 'BOOKINGS.ACTIONS.EXTEND_30' | translate }}</span>
                        </button>
                      }
                      <!-- Cancel and evict are the same decision at two stages of a booking's life,
                           so only ever one of them applies. -->
                      @if (canCancel(booking)) {
                        <button
                          type="button"
                          class="booking-panel-action is-danger"
                          *hasPermission="permissions.bookings.groups.general.components.cancel"
                          (click)="blockPanel.hide(); cancel(booking)"
                        >
                          <i aria-hidden="true" class="pi pi-times"></i>
                          <span class="booking-panel-action-label">{{ 'BOOKINGS.ACTIONS.CANCEL' | translate }}</span>
                        </button>
                      }
                      @if (canEvict(booking)) {
                        <button
                          type="button"
                          class="booking-panel-action is-danger"
                          *hasPermission="permissions.bookings.groups.general.components.evict"
                          (click)="blockPanel.hide(); evict(booking)"
                        >
                          <i aria-hidden="true" class="pi pi-sign-out"></i>
                          <span class="booking-panel-action-label">{{ 'BOOKINGS.ACTIONS.EVICT' | translate }}</span>
                        </button>
                      }
                    </div>
                  }
                </div>
              }
            </p-popover>
          </p-tabpanel>

          <!-- ───────────────────────────── Grid ───────────────────────────── -->
          <p-tabpanel [value]="tabs.grid">
            <!-- Paged, not endlessly scrolled: a week of a busy building is hundreds of rows.
                 `alwaysShowPaginator` off so a client with six bookings is not shown a pager. -->
            <p-table
              class="table-bookings"
              styleClass="p-datatable-striped"
              [value]="filteredBookings"
              [loading]="loading"
              [scrollable]="true"
              scrollHeight="calc(100vh - 470px)"
              [paginator]="true"
              [alwaysShowPaginator]="false"
              [rows]="rowsPerPage"
              [rowsPerPageOptions]="rowsPerPageOptions"
              [showCurrentPageReport]="true"
              [currentPageReportTemplate]="paginationTemplate"
            >
              <ng-template pTemplate="header">
                <tr>
                  <th pSortableColumn="area_name">{{ 'BOOKINGS.FIELDS.ROOM' | translate }} <p-sortIcon field="area_name"></p-sortIcon></th>
                  <th pSortableColumn="start_time">{{ 'BOOKINGS.FIELDS.WHEN' | translate }} <p-sortIcon field="start_time"></p-sortIcon></th>
                  <th pSortableColumn="pass_name">{{ 'BOOKINGS.FIELDS.BOOKED_BY' | translate }} <p-sortIcon field="pass_name"></p-sortIcon></th>
                  <th pSortableColumn="client_role_name">{{ 'BOOKINGS.FIELDS.GROUP' | translate }} <p-sortIcon field="client_role_name"></p-sortIcon></th>
                  <th pSortableColumn="title">{{ 'BOOKINGS.FIELDS.TITLE' | translate }} <p-sortIcon field="title"></p-sortIcon></th>
                  <!-- A client whose rooms are all priced at 0 has no credit system; a column of
                       zeroes only invites the question of what it is for. -->
                  @if (usesCredits) {
                    <th class="text-center col-credits">{{ creditsUnitKey | translate }}</th>
                  }
                  <th pSortableColumn="status">{{ 'BOOKINGS.FIELDS.STATUS' | translate }} <p-sortIcon field="status"></p-sortIcon></th>
                  <th class="col-actions"></th>
                </tr>
              </ng-template>

              <ng-template pTemplate="body" let-booking>
                <tr class="fz-14">
                  <td class="cell-room">
                    <span class="cell-room-name" dir="auto">{{ booking.area_name || ('BOOKINGS.UNKNOWN_ROOM' | translate) }}</span>
                    <!-- The branch is what tells six rooms called "Room B" apart. -->
                    @if (hasMultipleBranches && booking.event_name) {
                      <span class="cell-room-branch" dir="auto">{{ booking.event_name }}</span>
                    }
                  </td>
                  <!-- start_wall / end_wall, never start_time: the times on this page are the
                       CLIENT'S, which is the clock PAPI validates the booking against.
                       dir="ltr" or the bidi algorithm reorders "date start – end" into "end – start". -->
                  <td>
                    <span class="cell-when" dir="ltr">
                      <span class="cell-when-date">{{ booking.start_wall | date: 'dd/MM/yyyy' }}</span>
                      <span class="cell-when-time">{{ booking.start_wall | date: 'HH:mm' }}&ndash;{{ booking.end_wall | date: 'HH:mm' }}</span>
                    </span>
                  </td>
                  <td>{{ booking.pass_name || '—' }}</td>
                  <td>{{ booking.client_role_name || '—' }}</td>
                  <td>{{ booking.title || '—' }}</td>
                  @if (usesCredits) {
                    <td class="text-center">
                      <span dir="ltr">{{ booking.credits_charged - booking.credits_refunded }}</span>
                      @if (booking.credits_refunded > 0) {
                        <span class="cell-refunded">{{ 'BOOKINGS.FIELDS.REFUNDED' | translate }} {{ booking.credits_refunded }}</span>
                      }
                    </td>
                  }
                  <td><p-tag [severity]="statusSeverity(booking.status)" [value]="'BOOKINGS.STATUS.' + (booking.status | uppercase) | translate"></p-tag></td>
                  <td>
                    <div class="row-actions">
                      @if (canEdit(booking)) {
                        <button
                          type="button"
                          class="btn btn-icon primary transparent"
                          *hasPermission="permissions.bookings.groups.general.components.create"
                          [title]="'COMMON.BUTTONS.EDIT' | translate"
                          (click)="edit(booking)"
                        >
                          <i aria-hidden="true" class="icon icon-pencil"></i>
                          <span class="sr-only">{{ 'COMMON.BUTTONS.EDIT' | translate }}</span>
                        </button>
                      }
                      @if (canEdit(booking)) {
                        <button
                          type="button"
                          class="btn btn-icon primary transparent"
                          *hasPermission="permissions.bookings.groups.general.components.extend"
                          [title]="'BOOKINGS.ACTIONS.EXTEND_30' | translate"
                          (click)="extend(booking)"
                        >
                          <i aria-hidden="true" class="pi pi-clock"></i>
                          <span class="sr-only">{{ 'BOOKINGS.ACTIONS.EXTEND_30' | translate }}</span>
                        </button>
                      }
                      @if (canCancel(booking)) {
                        <button
                          type="button"
                          class="btn btn-icon danger transparent"
                          *hasPermission="permissions.bookings.groups.general.components.cancel"
                          [title]="'BOOKINGS.ACTIONS.CANCEL' | translate"
                          (click)="cancel(booking)"
                        >
                          <i aria-hidden="true" class="pi pi-times"></i>
                          <span class="sr-only">{{ 'BOOKINGS.ACTIONS.CANCEL' | translate }}</span>
                        </button>
                      }
                      @if (canEvict(booking)) {
                        <button
                          type="button"
                          class="btn btn-icon danger transparent"
                          *hasPermission="permissions.bookings.groups.general.components.evict"
                          [title]="'BOOKINGS.ACTIONS.EVICT' | translate"
                          (click)="evict(booking)"
                        >
                          <i aria-hidden="true" class="pi pi-sign-out"></i>
                          <span class="sr-only">{{ 'BOOKINGS.ACTIONS.EVICT' | translate }}</span>
                        </button>
                      }
                    </div>
                  </td>
                </tr>
              </ng-template>

              <ng-template pTemplate="emptymessage">
                <tr>
                  <td [attr.colspan]="usesCredits ? 8 : 7">
                    <p-message severity="info" class="w-stretch">{{ 'BOOKINGS.NO_RESULTS' | translate }}</p-message>
                  </td>
                </tr>
              </ng-template>
            </p-table>
          </p-tabpanel>

          <!-- ────────────────────────── Usage report ───────────────────────── -->
          <p-tabpanel [value]="tabs.usage">
            <div class="usage-toolbar">
              <div class="filter-field">
                <label class="filter-label" for="usage-group-by">{{ 'BOOKINGS.USAGE.GROUP_BY' | translate }}</label>
                <p-select
                  inputId="usage-group-by"
                  optionLabel="label"
                  optionValue="value"
                  [options]="groupByOptions"
                  [(ngModel)]="usageGroupBy"
                  (onChange)="loadUsage()"
                ></p-select>
              </div>
            </div>

            <p-table class="table-bookings-usage" styleClass="p-datatable-striped" [value]="usageRows" [loading]="usageLoading">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ 'BOOKINGS.USAGE.GROUP' | translate }}</th>
                  <th class="text-center">{{ 'BOOKINGS.USAGE.BOOKINGS' | translate }}</th>
                  <th class="text-center">{{ 'BOOKINGS.USAGE.NO_SHOWS' | translate }}</th>
                  <!-- The money half of the report. A client that meters nothing still wants the
                       two counts above it — who is booking, and who is not turning up. -->
                  @if (usesCredits) {
                    <th class="text-center">{{ 'BOOKINGS.USAGE.CHARGED' | translate }}</th>
                    <th class="text-center">{{ 'BOOKINGS.USAGE.REFUNDED' | translate }}</th>
                    <th class="text-center">{{ 'BOOKINGS.USAGE.NET' | translate }} ({{ creditsUnitKey | translate }})</th>
                    <!-- Only the group grouping HAS an allowance; a person and a room do not. -->
                    @if (usageGroupBy === 'client_role') {
                      <th class="text-center">{{ 'BOOKINGS.USAGE.ALLOWANCE' | translate }}</th>
                    }
                  }
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr class="fz-14">
                  <td>{{ row.group_name || ('BOOKINGS.USAGE.UNKNOWN_GROUP' | translate) + ' #' + row.group_id }}</td>
                  <td class="text-center"><span dir="ltr">{{ row.bookings_count }}</span></td>
                  <td class="text-center"><span dir="ltr">{{ row.no_show_count }}</span></td>
                  @if (usesCredits) {
                    <td class="text-center"><span dir="ltr">{{ row.credits_charged }}</span></td>
                    <td class="text-center"><span dir="ltr">{{ row.credits_refunded }}</span></td>
                    <td class="text-center"><strong dir="ltr">{{ row.credits_net }}</strong></td>
                    @if (usageGroupBy === 'client_role') {
                      <td class="text-center">
                        <!-- "12 / monthly", or "Unlimited" — never 0, which would read as "may not book". -->
                        @if (row.allowance_credits !== null && row.allowance_credits !== undefined) {
                          <span class="usage-allowance" dir="ltr">{{ row.allowance_credits }}</span>
                          <!-- Same wording as the role settings panel that sets it — one concept, one phrase. -->
                          <span class="usage-allowance-period">{{ 'SETTINGS.LABELS.BOOKING.PERIOD_' + (row.allowance_period | uppercase) | translate }}</span>
                        } @else {
                          <span class="color-gray2">{{ 'BOOKINGS.USAGE.NO_ALLOWANCE' | translate }}</span>
                        }
                      </td>
                    }
                  }
                </tr>
              </ng-template>
              <ng-template pTemplate="emptymessage">
                <tr>
                  <td [attr.colspan]="usageColumnCount">
                    <p-message severity="info" class="w-stretch">{{ 'BOOKINGS.NO_RESULTS' | translate }}</p-message>
                  </td>
                </tr>
              </ng-template>
            </p-table>
          </p-tabpanel>
        </p-tabpanels>
      </p-tabs>
    </div>
  </div>
</div>

<!-- ConfirmationService is provided app-wide but each page hosts its own dialog. -->
<p-confirmDialog></p-confirmDialog>

<app-booking-form #bookingForm (saved)="onFormSaved()"></app-booking-form>
